import React, { useState, useEffect, useRef } from "react";
import { Button, Col, Form, Modal, Row, Card, Badge, Alert, Dropdown } from "react-bootstrap";
import { Formik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import "react-toastify/dist/ReactToastify.css";
import DocumentDropZoneUploader from "../../../../../components/DocumentDropZoneUploader";
import UnlayerDocumentEditor from "../../../../../components/UnlayerDocumentEditor";
import { fetchTemplates, fetchTemplateById, fetchDocumentTypes, createTemplate } from "../../../../../services/recruitmentService";

const DOCUMENT_ACCEPT = {
  "application/pdf": [".pdf"],
  "application/msword": [".doc"],
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [
    ".docx",
  ],
};

// Centralized placeholder configuration
const PLACEHOLDER_CONFIG = {
  candidate: {
    label: 'Candidate Information',
    icon: 'fa-user',
    color: 'primary',
    placeholders: [
      { key: 'candidate_name', label: 'Full Name', fallback: '[Candidate Name]', path: ['full_name', 'name'] },
      { key: 'first_name', label: 'First Name', fallback: '[First Name]', path: ['first_name'] },
      { key: 'last_name', label: 'Last Name', fallback: '[Last Name]', path: ['last_name'] },
      { key: 'email', label: 'Email Address', fallback: '[Email Address]', path: ['email'] },
      { key: 'phone', label: 'Phone Number', fallback: '[Phone Number]', path: ['phone', 'mobile'] },
      { key: 'address', label: 'Address', fallback: '[Address]', path: ['address'] },
      { key: 'city', label: 'City', fallback: '[City]', path: ['city'] },
      { key: 'state', label: 'State', fallback: '[State]', path: ['state'] }
    ]
  },
  job: {
    label: 'Job Information',
    icon: 'fa-briefcase',
    color: 'success',
    placeholders: [
      { key: 'designation', label: 'Job Title/Position', fallback: '[Designation]', path: ['position', 'job_title', 'designation'] },
      { key: 'department', label: 'Department', fallback: '[Department]', path: ['department'] },
      { key: 'experience', label: 'Experience', fallback: '[Experience]', path: ['experience'] },
      { key: 'current_salary', label: 'Current Salary', fallback: '[Current Salary]', path: ['current_salary'] },
      { key: 'expected_salary', label: 'Expected Salary', fallback: '[Expected Salary]', path: ['expected_salary'] }
    ]
  },
  company: {
    label: 'Company Information',
    icon: 'fa-building',
    color: 'info',
    placeholders: [
      { key: 'company_name', label: 'Company Name', fallback: '[Company Name]', path: [] },
      { key: 'hr_name', label: 'HR Name', fallback: '[HR Name]', path: [] },
      { key: 'joining_date', label: 'Joining Date', fallback: '[Joining Date]', path: [] },
      { key: 'ctc', label: 'CTC Amount', fallback: '[CTC Amount]', path: [] },
      { key: 'interview_date', label: 'Interview Date', fallback: '[Interview Date]', path: [] },
      { key: 'offer_date', label: 'Offer Date', fallback: '[Offer Date]', path: [] }
    ]
  }
};

const CreateDocumentModal = ({
  show,
  candidateName,
  candidateData,
  onClose,
  onAction,
  submitting = false,
}) => {
  const [documentSource, setDocumentSource] = useState("uploaded"); // "uploaded" or "generated"
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [documentTypesLoading, setDocumentTypesLoading] = useState(false);
  const [showCreateTemplate, setShowCreateTemplate] = useState(false);
  const [creatingTemplate, setCreatingTemplate] = useState(false);
  const [editorLoading, setEditorLoading] = useState(true);
  const editorRef = useRef(null);
  const createTemplateEditorRef = useRef(null);
  const templateSelectionCallbackRef = useRef(null);
  const [showPlaceholderDropdown, setShowPlaceholderDropdown] = useState(false);
  const [activeEditor, setActiveEditor] = useState('main'); // 'main' or 'create'

  const uploadSchema = Yup.object({
    document_type_id: Yup.string().required("Please select document type"),
    attachment: Yup.mixed().required("Please upload a document"),
    title: Yup.string().max(100, "Maximum 100 characters allowed"),
  });

  const templateSchema = Yup.object({
    template_id: Yup.string().required("Please select a template"),
    subject: Yup.string().required("Subject is required"),
    // content validation handled manually since it comes from Unlayer editor
    format: Yup.string().required("Please select document format"),
  });

  const getSchema = () => documentSource === "uploaded" ? uploadSchema : templateSchema;

  const getInitialValues = () => {
    if (documentSource === "uploaded") {
      return {
        document_type_id: "",
        attachment: null,
        title: "",
      };
    }
    return {
      template_id: "",
      subject: "",
      content: "",
      format: "pdf",
    };
  };

  // Load templates when modal opens and template option is selected
  useEffect(() => {
    if (show && documentSource === "generated") {
      loadTemplates();
    }
    if (show && documentSource === "uploaded") {
      loadDocumentTypes();
    }
  }, [show, documentSource]);

  const loadTemplates = async () => {
    setTemplatesLoading(true);
    try {
      const response = await fetchTemplates();
      setTemplates(response.data || []);
    } catch (error) {
      console.error("Error fetching templates:", error);
      toast.error("Unable to load templates");
      setTemplates([]);
    } finally {
      setTemplatesLoading(false);
    }
  };

  // Define loadDocumentTypes before using it in useEffect
  const loadDocumentTypes = async () => {
    setDocumentTypesLoading(true);
    try {
      const response = await fetchDocumentTypes();
      if (response.success) {
        const filteredDocTypes = (response.result || []).filter(
          (docType) => docType.employer_document_id === 1 || docType.employer_document_id === 2
        );
        setDocumentTypes(filteredDocTypes);
      } else {
        console.error('Failed to fetch document types:', response);
        toast.error('Unable to load document types');
      }
    } catch (error) {
      console.error('Error fetching document types:', error);
      toast.error('Unable to load document types');
    } finally {
      setDocumentTypesLoading(false);
    }
  };

  useEffect(() => {
    loadDocumentTypes();
  }, []);

  const handleTemplateSelect = async (templateId, setFieldValue) => {
    setEditorLoading(true); // Reset loading state
    
    if (!templateId) {
      setSelectedTemplate(null);
      setSelectedTemplateId("");
      setFieldValue('template_id', "");
      setFieldValue('subject', "");
      setFieldValue('content', "");
      setEditorLoading(false);
      return;
    }

    if (templateId === "create_new") {
      setShowCreateTemplate(true);
      setSelectedTemplate(null);
      setSelectedTemplateId("");
      setEditorLoading(false);
      return;
    }

    try {
      const response = await fetchTemplateById(templateId);
      const template = response.data;
      setSelectedTemplate(template);
      setSelectedTemplateId(templateId);
      setFieldValue('template_id', templateId);
      
      // Auto-populate template with candidate data
      const populatedSubject = populateTemplate(template.subject, candidateName, candidateData);
      let populatedContent = populateTemplate(template.content, candidateName, candidateData);
      
      setFieldValue('subject', populatedSubject);
      setFieldValue('template_type', template.template_type);
      
      // Load content in Unlayer editor for preview and editing
      setTimeout(() => {
        if (editorRef.current && editorRef.current.loadDesign) {
          try {
            console.log('Loading template in editor:', template.template_name);
            
            // Try to parse and load design JSON first for full design fidelity
            let designData = null;
            if (template.design_content) {
              try {
                designData = JSON.parse(template.design_content);
                console.log('Parsed design data successfully');
              } catch (parseError) {
                console.warn('Failed to parse design_content as JSON:', parseError);
              }
            }

            if (designData && designData.body) {
              // Load the full design with populated content
              const populatedDesign = populateDesignContent(designData, candidateName, candidateData);
              editorRef.current.loadDesign(populatedDesign);
              console.log('Loaded design with populated content');
            } else if (populatedContent) {
              // Create a simple design with the HTML content
              const simpleDesign = {
                body: {
                  rows: [{
                    id: 'row-1',
                    cells: [1],
                    columns: [{
                      id: 'column-1',
                      contents: [{
                        id: 'content-1',
                        type: 'html',
                        values: {
                          html: populatedContent
                        }
                      }]
                    }]
                  }]
                }
              };
              editorRef.current.loadDesign(simpleDesign);
              console.log('Loaded simple HTML design');
            } else {
              // Fallback to basic content
              const fallbackDesign = {
                body: {
                  rows: [{
                    id: 'row-1',
                    cells: [1],
                    columns: [{
                      id: 'column-1',
                      contents: [{
                        id: 'content-1',
                        type: 'text',
                        values: {
                          text: '<p>Start editing your document content here...</p>'
                        }
                      }]
                    }]
                  }]
                }
              };
              editorRef.current.loadDesign(fallbackDesign);
              console.log('Loaded fallback design');
            }
            
            setEditorLoading(false);
          } catch (error) {
            console.error('Error loading template in editor:', error);
            setEditorLoading(false);
          }
        } else {
          console.warn('Editor ref not available, retrying in 500ms');
          setTimeout(() => {
            if (editorRef.current && editorRef.current.loadDesign) {
              // Retry logic here
              const retryDesign = {
                body: {
                  rows: [{
                    id: 'row-1',
                    cells: [1],
                    columns: [{
                      id: 'column-1',
                      contents: [{
                        id: 'content-1',
                        type: 'text',
                        values: {
                          text: populatedContent || 'Start editing your document content here...'
                        }
                      }]
                    }]
                  }]
                }
              };
              editorRef.current.loadDesign(retryDesign);
              setEditorLoading(false);
            }
          }, 500);
        }
      }, 200);
      setFieldValue('content', populatedContent);
    } catch (error) {
      console.error("Error fetching template details:", error);
      toast.error("Unable to load template details");
      setSelectedTemplate(null);
      setSelectedTemplateId("");
    }
  };

  // Dynamic placeholder population using configuration
  const populateTemplate = (text, candidateName, candidateData) => {
    if (!text) return '';
    
    let populatedText = text;
    
    // Process all placeholder categories
    Object.values(PLACEHOLDER_CONFIG).forEach(category => {
      category.placeholders.forEach(placeholder => {
        const pattern = new RegExp(`{{${placeholder.key}}}`, 'g');
        let value = placeholder.fallback;
        
        // Special handling for candidate_name
        if (placeholder.key === 'candidate_name') {
          value = candidateName || getNestedValue(candidateData, placeholder.path) || placeholder.fallback;
        } else {
          // Try to get value from candidate data using the path array
          const dataValue = getNestedValue(candidateData, placeholder.path);
          if (dataValue) {
            value = dataValue;
          }
        }
        
        populatedText = populatedText.replace(pattern, value);
      });
    });
    
    return populatedText;
  };
  
  // Helper function to get nested values from object using path array
  const getNestedValue = (obj, pathArray) => {
    if (!obj || !pathArray || pathArray.length === 0) return null;
    
    for (const path of pathArray) {
      if (obj[path]) return obj[path];
    }
    return null;
  };
  
  // Insert placeholder into editor
  const insertPlaceholder = (placeholderKey) => {
    const placeholder = `{{${placeholderKey}}}`;
    const targetEditor = activeEditor === 'main' ? editorRef : createTemplateEditorRef;
    
    if (targetEditor.current) {
      try {
        // Get current design, add placeholder, and reload
        targetEditor.current.exportDesign().then((designData) => {
          // Create a simple design with placeholder if no content exists
          let newDesign;
          
          if (!designData || !designData.body || !designData.body.rows || designData.body.rows.length === 0) {
            // Create new design with placeholder
            newDesign = {
              body: {
                rows: [{
                  id: 'row-' + Date.now(),
                  cells: [1],
                  columns: [{
                    id: 'column-' + Date.now(),
                    contents: [{
                      id: 'content-' + Date.now(),
                      type: 'text',
                      values: {
                        text: `<p>${placeholder}</p>`
                      }
                    }]
                  }]
                }]
              }
            };
          } else {
            // Add placeholder to existing content
            newDesign = { ...designData };
            const lastRow = newDesign.body.rows[newDesign.body.rows.length - 1];
            const lastColumn = lastRow.columns[lastRow.columns.length - 1];
            const lastContent = lastColumn.contents[lastColumn.contents.length - 1];
            
            // Add placeholder to last content or create new content
            if (lastContent.type === 'text') {
              lastContent.values.text += ` ${placeholder}`;
            } else {
              lastColumn.contents.push({
                id: 'content-' + Date.now(),
                type: 'text',
                values: {
                  text: `<p>${placeholder}</p>`
                }
              });
            }
          }
          
          // Load the updated design
          targetEditor.current.loadDesign(newDesign);
          toast.success(`Inserted placeholder: ${placeholder}`);
        }).catch((error) => {
          console.warn('Could not export/modify design:', error);
          // Fallback: Copy to clipboard
          navigator.clipboard.writeText(placeholder).then(() => {
            toast.success(`Copied to clipboard: ${placeholder}`);
          }).catch(() => {
            toast.info(`Placeholder: ${placeholder}`);
          });
        });
      } catch (error) {
        console.warn('Could not insert placeholder:', error);
        // Fallback: Copy to clipboard
        navigator.clipboard.writeText(placeholder).then(() => {
          toast.success(`Copied to clipboard: ${placeholder}`);
        }).catch(() => {
          toast.info(`Placeholder: ${placeholder}`);
        });
      }
    } else {
      // Editor not available, copy to clipboard
      navigator.clipboard.writeText(placeholder).then(() => {
        toast.success(`Copied to clipboard: ${placeholder}`);
      }).catch(() => {
        toast.info(`Placeholder: ${placeholder}`);
      });
    }
    
    setShowPlaceholderDropdown(false);
  };

  const populateDesignContent = (design, candidateName, candidateData) => {
    if (!design) return design;
    
    // Create a deep copy to avoid mutating original
    const designCopy = JSON.parse(JSON.stringify(design));
    
    // Recursively traverse the design object and populate text content
    const traverse = (obj) => {
      if (typeof obj === 'string') {
        return populateTemplate(obj, candidateName, candidateData);
      }
      if (Array.isArray(obj)) {
        return obj.map(item => traverse(item));
      }
      if (obj && typeof obj === 'object') {
        const result = {};
        for (const key in obj) {
          if (obj.hasOwnProperty(key)) {
            if (key === 'text' || key === 'html') {
              result[key] = populateTemplate(obj[key] || '', candidateName, candidateData);
            } else {
              result[key] = traverse(obj[key]);
            }
          }
        }
        return result;
      }
      return obj;
    };
    
    return traverse(designCopy);
  };



  const handleEditorLoad = (editor) => {
    console.log('Editor loaded:', editor);
    setEditorLoading(false);
  };

  const handleEditorReady = (editor) => {
    // Editor is fully loaded and ready
    console.log('Editor ready:', editor);
    setEditorLoading(false);
  };

  const handleCreateTemplate = async (templateData) => {
    setCreatingTemplate(true);
    try {
      // Get content from create template editor
      let htmlContent = templateData.content || '';
      let designJson = null;

      if (createTemplateEditorRef.current) {
        try {
          const [htmlData, designData] = await Promise.all([
            createTemplateEditorRef.current.exportHtml(),
            createTemplateEditorRef.current.exportDesign()
          ]);
          htmlContent = htmlData.html || htmlContent;
          designJson = designData;
        } catch (editorError) {
          console.warn('Editor export failed, using form content:', editorError);
        }
      } else {
        console.error('Editor reference is not initialized.');
      }

      if (!htmlContent.trim()) {
        console.error('Content is empty. Please check the editor initialization or input.');
        toast.error('Content cannot be empty. Please add content to the template.');
        return;
      }

      const payload = {
        template_name: templateData.template_name,
        template_type: templateData.template_type,
        document_type_id: templateData.document_type_id,
        subject: templateData.subject,
        content: htmlContent,
        design_content: designJson ? JSON.stringify(designJson) : null
      };

      console.log('Creating template with payload:', payload);
      const response = await createTemplate(payload);
      toast.success("Template created successfully!");

      // Refresh templates list
      await loadTemplates();

      setShowCreateTemplate(false);
      
      // Set the newly created template as selected and load its content
      const newTemplateId = response.data?.id || response.id;
      if (newTemplateId && templateSelectionCallbackRef.current) {
        // Use the callback to properly load the newly created template
        templateSelectionCallbackRef.current(newTemplateId);
      }
    } catch (error) {
      console.error("Error creating template:", error);
      toast.error("Failed to create template");
    } finally {
      setCreatingTemplate(false);
    }
  };

  const handleModalClose = () => {
    setDocumentSource("uploaded");
    setSelectedTemplate(null);
    setSelectedTemplateId("");
    setTemplates([]);
    setDocumentTypes([]);
    setShowCreateTemplate(false);
    onClose();
  };

  return (
    <Modal show={show} onHide={handleModalClose} centered size="xl" backdrop="static">
      <Modal.Header  className="py-2">
        <Modal.Title className="fs-5">
          <i className="fa fa-plus me-2"></i>
          Create Document
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-3">
        {/* Document Source Selection */}
        <div className="mb-3">
          <Form.Label className="fw-semibold mb-2">Create Document</Form.Label>
          <div className="d-flex gap-3">
            <Form.Check
              type="radio"
              id="upload-option"
              name="documentSource"
              label="Upload Document"
              checked={documentSource === "uploaded"}
              onChange={() => setDocumentSource("uploaded")}
            />
            <Form.Check
              type="radio"
              id="template-option"
              name="documentSource"
              label="Generate from Template"
              checked={documentSource === "generated"}
              onChange={() => setDocumentSource("generated")}
            />
          </div>
        </div>

        <Formik
          key={documentSource} // Force re-initialization when source changes
          initialValues={getInitialValues()}
          validationSchema={getSchema()}
          onSubmit={async (values, helpers) => {
            try {
              let payload;
              if (documentSource === "generated") {
                // Get edited content from Unlayer editor
                let htmlContent = values.content;
                let designJson = null;
                
                if (editorRef.current) {
                  try {
                    // Export both HTML and design from the editor (this captures user edits)
                    const [htmlData, designData] = await Promise.all([
                      editorRef.current.exportHtml(),
                      editorRef.current.exportDesign()
                    ]);
                    
                    htmlContent = htmlData?.html || htmlContent;
                    designJson = designData;
                    
                    console.log('Exported content from editor:', {
                      htmlLength: htmlContent?.length,
                      hasDesign: !!designJson
                    });
                  } catch (error) {
                    console.error('Error exporting editor content:', error);
                    toast.warn('Could not export editor content, using fallback content');
                  }
                }
                
                // Validate content exists
                if (!htmlContent || htmlContent.trim() === '' || htmlContent.trim() === '<div></div>' || htmlContent.trim() === '<p></p>') {
                  toast.error('Content is required. Please add content to the document.');
                  helpers.setSubmitting(false);
                  return;
                }
                
                payload = {
                  template_id: values.template_id,
                  subject: values.subject,
                  content: htmlContent, // This now contains the edited content from Unlayer
                  design_content: designJson ? JSON.stringify(designJson) : null,
                  format: values.format,
                  template_type: selectedTemplate?.template_type,
                  candidate_name: candidateName,
                  candidate_data: candidateData,
                  source: "generated"
                };
              } else {
                payload = {
                  ...values,
                  source: "uploaded"
                };
              }
              await onAction(payload);
              helpers.resetForm();
            } finally {
              helpers.setSubmitting(false);
            }
          }}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            setFieldValue,
            isSubmitting,
          }) => {
            // Set up callback for template selection after creation
            templateSelectionCallbackRef.current = (templateId) => {
              handleTemplateSelect(templateId, setFieldValue);
            };
            
            return (
            <Form noValidate onSubmit={handleSubmit}>
              {documentSource === "uploaded" ? (
                // Upload Document Form
                <>
                  <Row className="mb-2">
                    <Col md={6}>
                      <Form.Group className="mb-2">
                        <Form.Label className="mb-1">
                          Document Type <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Select
                          name="document_type_id"
                          value={values.document_type_id}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          isInvalid={touched.document_type_id && !!errors.document_type_id}
                          disabled={documentTypesLoading}
                        >
                          <option value="">
                            {documentTypesLoading ? "Loading document types..." : "Select type"}
                          </option>
                          {documentTypes.map((docType) => (
                            <option key={docType.id} value={docType.employer_document_id}>
                              {docType.document}
                            </option>
                          ))}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">
                          {errors.document_type_id}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-2">
                        <Form.Label className="mb-1">Document Title</Form.Label>
                        <Form.Control
                          type="text"
                          name="title"
                          value={values.title || ''}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          placeholder="Enter document title"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Form.Group>
                    <Form.Label>
                      Upload Document <span className="text-danger">*</span>
                    </Form.Label>
                    <DocumentDropZoneUploader
                      documentKey="attachment"
                      uploadedFile={values.attachment}
                      onFileSelect={(key, file) => setFieldValue(key, file)}
                      onRemove={(key) => setFieldValue(key, null)}
                      acceptedTypes={DOCUMENT_ACCEPT}
                      helperText="Only PDF/DOC/DOCX formats up to 5MB are allowed."
                    />
                    {touched.attachment && errors.attachment && (
                      <div className="text-danger small mt-2">
                        {errors.attachment}
                      </div>
                    )}
                  </Form.Group>
                </>
              ) : (
                // Template Selection Form
                <>
                  <Row className="mb-2">
                    <Col md={6}>
                      <Form.Group className="mb-2">
                        <Form.Label className="mb-1">
                          Select Template <span className="text-danger">*</span>
                        </Form.Label>
                        <Dropdown>
                          <Dropdown.Toggle
                            variant="outline-primary"
                            className="w-100 text-start"
                            style={{ height: "38px" }}
                            disabled={templatesLoading}
                          >
                            {templatesLoading ? (
                              <i className="fa fa-spinner fa-spin me-2"></i>
                            ) : null}
                            {templatesLoading 
                              ? "Loading templates..." 
                              : selectedTemplateId
                                ? (() => {
                                    const selectedTemplate = templates.find((t) => t.id === selectedTemplateId);
                                    if (!selectedTemplate) return "Template not found";
                                    return `${selectedTemplate.template_name || 'Untitled'} (${selectedTemplate.template_type || 'Unknown'})`;
                                  })()
                                : "Select a template"
                            }
                          </Dropdown.Toggle>
                          <Dropdown.Menu className="w-100">
                            <Dropdown.Item 
                              onClick={() => handleTemplateSelect("create_new", setFieldValue)}
                              className="text-primary fw-bold"
                            >
                              <i className="fa fa-plus me-2"></i>
                              Create New Template
                            </Dropdown.Item>
                            {templates && templates.length > 0 && (
                              <>
                                <Dropdown.Divider />
                                {templates.map((template) => (
                                  <Dropdown.Item
                                    key={template.id || Math.random()}
                                    onClick={() => handleTemplateSelect(template.id, setFieldValue)}
                                  >
                                    {template.template_name || 'Untitled'} ({template.template_type || 'Unknown'})
                                  </Dropdown.Item>
                                ))}
                              </>
                            )}
                          </Dropdown.Menu>
                        </Dropdown>
                        {touched.template_id && errors.template_id && (
                          <div className="text-danger small mt-1">
                            {errors.template_id}
                          </div>
                        )}
                        {/* Hidden field for Formik validation */}
                        <Form.Control
                          type="hidden"
                          name="template_id"
                          value={values.template_id}
                          onChange={handleChange}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-2">
                        <Form.Label className="mb-1">
                          Format <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Select
                          size="sm"
                          name="format"
                          value={values.format}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          isInvalid={touched.format && !!errors.format}
                        >
                          <option value="pdf">PDF Document</option>
                          {/* <option value="docx">Word Document</option> */}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">
                          {errors.format52}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  </Row>

                  {showCreateTemplate && !selectedTemplate && (
                    <Card className="mb-3 border-primary">
                      <Card.Body className="p-3">
                        <Formik
                          initialValues={{
                            template_name: "",
                            template_type: "offer_letter",
                            document_type_id: "",
                            subject: "",
                            content: "",
                          }}
                          validationSchema={Yup.object({
                            template_name: Yup.string().required("Template name is required"),
                            template_type: Yup.string().required("Template type is required"),
                            document_type_id: Yup.string().required("Document type is required"),
                            subject: Yup.string().required("Subject is required"),
                            // content validation handled manually in submit handler
                          })}
                          onSubmit={async (templateValues, helpers) => {
                            await handleCreateTemplate(templateValues);
                            helpers.resetForm();
                          }}
                        >
                          {({
                            values: templateValues,
                            errors: templateErrors,
                            touched: templateTouched,
                            handleChange: templateHandleChange,
                            handleBlur: templateHandleBlur,
                            handleSubmit: templateHandleSubmit,
                            isSubmitting: templateSubmitting,
                          }) => (
                            <div>
                              <Row>
                                <Col md={6}>
                                  <Form.Group className="mb-2">
                                    <Form.Label className="small fw-semibold">Template Name *</Form.Label>
                                    <Form.Control
                                      size="sm"
                                      type="text"
                                      name="template_name"
                                      value={templateValues.template_name}
                                      onChange={templateHandleChange}
                                      onBlur={templateHandleBlur}
                                      isInvalid={templateTouched.template_name && !!templateErrors.template_name}
                                      placeholder="Enter template name"
                                    />
                                    <Form.Control.Feedback type="invalid">
                                      {templateErrors.template_name}
                                    </Form.Control.Feedback>
                                  </Form.Group>
                                </Col>
                                <Col md={6}>
                                  <Form.Group className="mb-2">
                                    <Form.Label className="small fw-semibold">Template Type *</Form.Label>
                                    <Form.Select
                                      size="sm"
                                      name="template_type"
                                      value={templateValues.template_type}
                                      onChange={(e) => {
                                        templateHandleChange(e);
                                        // Also set document_type_id when template_type changes
                                        const selectedDocType = documentTypes.find(dt => dt.document === e.target.value);
                                        if (selectedDocType) {
                                          templateHandleChange({
                                            target: { name: 'document_type_id', value: selectedDocType.employer_document_id }
                                          });
                                        }
                                      }}
                                      onBlur={templateHandleBlur}
                                      isInvalid={templateTouched.template_type && !!templateErrors.template_type}
                                      disabled={documentTypesLoading}
                                    >
                                      <option value="">{documentTypesLoading ? 'Loading...' : 'Select Template Type'}</option>
                                      {documentTypes.map((docType) => (
                                        <option key={docType.employer_document_id} value={docType.document}>
                                          {docType.document}
                                        </option>
                                      ))}
                                    </Form.Select>
                                    <Form.Control.Feedback type="invalid">
                                      {templateErrors.template_type}
                                    </Form.Control.Feedback>
                                  </Form.Group>
                                </Col>
                              </Row>
                              <Form.Group className="mb-2">
                                <Form.Label className="small fw-semibold">Subject *</Form.Label>
                                <Form.Control
                                  size="sm"
                                  type="text"
                                  name="subject"
                                  value={templateValues.subject}
                                  onChange={templateHandleChange}
                                  onBlur={templateHandleBlur}
                                  isInvalid={templateTouched.subject && !!templateErrors.subject}
                                  placeholder="Enter template subject (e.g., Job Offer - {{candidate_name}})"
                                />
                                <Form.Control.Feedback type="invalid">
                                  {templateErrors.subject}
                                </Form.Control.Feedback>
                              </Form.Group>
                              <Form.Group className="mb-3">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                  <Form.Label className="small fw-semibold mb-0">Content *</Form.Label>
                                  <Dropdown show={showPlaceholderDropdown && activeEditor === 'create'} onToggle={() => setShowPlaceholderDropdown(!showPlaceholderDropdown)}>
                                    <Dropdown.Toggle 
                                      variant="outline-secondary" 
                                      size="sm"
                                      onClick={() => {
                                        setActiveEditor('create');
                                        setShowPlaceholderDropdown(!showPlaceholderDropdown);
                                      }}
                                    >
                                      <i className="fa fa-plus me-1"></i>
                                      Insert Placeholder
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu className="p-0" style={{ minWidth: '300px', maxHeight: '400px', overflowY: 'auto' }}>
                                      {Object.entries(PLACEHOLDER_CONFIG).map(([categoryKey, category]) => (
                                        <div key={categoryKey}>
                                          <div className={`dropdown-header bg-${category.color} text-white small`}>
                                            <i className={`fa ${category.icon} me-2`}></i>
                                            {category.label}
                                          </div>
                                          {category.placeholders.map((placeholder) => (
                                            <Dropdown.Item
                                              key={placeholder.key}
                                              onClick={() => insertPlaceholder(placeholder.key)}
                                              className="py-2"
                                            >
                                              <div className="d-flex justify-content-between align-items-center">
                                                <div>
                                                  <div className="fw-medium">{placeholder.label}</div>
                                                  <small className="text-muted">{`{{${placeholder.key}}}`}</small>
                                                </div>
                                                <Badge bg="light" text="dark" className="small">
                                                  {placeholder.fallback}
                                                </Badge>
                                              </div>
                                            </Dropdown.Item>
                                          ))}
                                        </div>
                                      ))}
                                    </Dropdown.Menu>
                                  </Dropdown>
                                </div>
                                <UnlayerDocumentEditor
                                  ref={createTemplateEditorRef}
                                  height={300}
                                  minHeight={350}
                                  initialContent={templateValues.content}
                                  placeholder="Design your template content with merge tags..."
                                  className=""
                                />
                              </Form.Group>
                              <Alert variant="info" className="py-2 mb-2">
                                <small>
                                  <strong>💡 Tip:</strong> Use the "Insert Placeholder" button above to add dynamic content. 
                                  Available categories: Candidate Info, Job Details, Company Data. 
                                  Placeholders will be automatically filled when generating documents.
                                </small>
                              </Alert>
                              <div className="d-flex justify-content-end gap-2">
                                <Button
                                  variant="outline-secondary"
                                  size="sm"
                                  onClick={() => setShowCreateTemplate(false)}
                                  disabled={templateSubmitting || creatingTemplate}
                                >
                                  Cancel
                                </Button>
                                <Button
                                  variant="primary"
                                  size="sm"
                                  onClick={templateHandleSubmit}
                                  disabled={templateSubmitting || creatingTemplate}
                                >
                                  {templateSubmitting || creatingTemplate ? (
                                    <>
                                      <i className="fa fa-spinner fa-spin me-2"></i>
                                      Creating...
                                    </>
                                  ) : (
                                    <>
                                      <i className="fa fa-save me-2"></i>
                                      Create Template
                                    </>
                                  )}
                                </Button>
                              </div>
                            </div>
                          )}
                        </Formik>
                      </Card.Body>
                    </Card>
                  )}

                  {/* Template Editor */}
                  {selectedTemplate && (
                    <Card className="mb-2">
                      <Card.Header className="d-flex justify-content-between align-items-center py-2">
                        <h6 className="mb-0 small">Document Content Editor</h6>
                        <Badge bg="primary" className="small">
                          {selectedTemplate.template_type}
                        </Badge>
                      </Card.Header>
                      <Card.Body className="p-2">
                        <Form.Group className="mb-2">
                          <Form.Label className="mb-1 small fw-semibold">Subject:</Form.Label>
                          <Form.Control
                            size="sm"
                            type="text"
                            name="subject"
                            value={values.subject}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            isInvalid={touched.subject && !!errors.subject}
                            placeholder="Enter document subject"
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.subject}
                          </Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group className="mb-2">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <Form.Label className="mb-0 small fw-semibold">Content:</Form.Label>
                            <Dropdown show={showPlaceholderDropdown && activeEditor === 'main'} onToggle={() => setShowPlaceholderDropdown(!showPlaceholderDropdown)}>
                              <Dropdown.Toggle 
                                variant="outline-primary" 
                                size="sm"
                                onClick={() => {
                                  setActiveEditor('main');
                                  setShowPlaceholderDropdown(!showPlaceholderDropdown);
                                }}
                              >
                                <i className="fa fa-plus me-1"></i>
                                Insert Placeholder
                              </Dropdown.Toggle>
                              <Dropdown.Menu className="p-0" style={{ minWidth: '300px', maxHeight: '400px', overflowY: 'auto' }}>
                                {Object.entries(PLACEHOLDER_CONFIG).map(([categoryKey, category]) => (
                                  <div key={categoryKey}>
                                    <div className={`dropdown-header bg-${category.color} text-white small`}>
                                      <i className={`fa ${category.icon} me-2`}></i>
                                      {category.label}
                                    </div>
                                    {category.placeholders.map((placeholder) => (
                                      <Dropdown.Item
                                        key={placeholder.key}
                                        onClick={() => insertPlaceholder(placeholder.key)}
                                        className="py-2"
                                      >
                                        <div className="d-flex justify-content-between align-items-center">
                                          <div>
                                            <div className="fw-medium">{placeholder.label}</div>
                                            <small className="text-muted">{`{{${placeholder.key}}}`}</small>
                                          </div>
                                          <Badge bg="light" text="dark" className="small">
                                            {placeholder.fallback}
                                          </Badge>
                                        </div>
                                      </Dropdown.Item>
                                    ))}
                                  </div>
                                ))}
                              </Dropdown.Menu>
                            </Dropdown>
                          </div>
                          <UnlayerDocumentEditor
                            ref={editorRef}
                            height={300}
                            minHeight={350}
                            initialContent={values.content}
                            onLoad={handleEditorLoad}
                            onReady={handleEditorReady}
                            loading={editorLoading}
                            placeholder="Design your document content here..."
                            className={touched.content && errors.content ? 'is-invalid' : ''}
                          />
                          {touched.content && errors.content && (
                            <div className="text-danger small mt-1">
                              {errors.content}
                            </div>
                          )}
                          {/* Hidden field for Formik validation */}
                          <Form.Control
                            type="hidden"
                            name="content"
                            value={values.content}
                            onChange={handleChange}
                          />
                        </Form.Group>
                        <Alert variant="info" className="mb-0 py-1">
                          <small>
                            <i className="fa fa-magic me-1"></i>
                            Edit the content above. Use "Insert Placeholder" to add dynamic data that will be populated when creating documents.
                          </small>
                        </Alert>
                      </Card.Body>
                    </Card>
                  )}
                </>
              )}

              <div className="text-muted small mt-2 mb-2">
                <i className="fa fa-user me-1"></i>
                Creating document for: <strong>{candidateName}</strong>
              </div>

              <div className="d-flex justify-content-end gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleModalClose}
                  disabled={isSubmitting || submitting}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  type="submit"
                  disabled={isSubmitting || submitting}
                >
                  {isSubmitting || submitting ? (
                    <>
                      <i className="fa fa-spinner fa-spin me-2"></i>
                      Creating...
                    </>
                  ) : (
                    <>
                      <i className="fa fa-plus me-2"></i>
                      Create Document
                    </>
                  )}
                </Button>
              </div>
            </Form>
            );
          }}
        </Formik>
      </Modal.Body>
    </Modal>
  );
};

export default CreateDocumentModal;